import React, { useEffect, useMemo, useState } from 'react';
import { useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { useTableSort } from '../../hooks/useTableSort';
import { normalizeName } from '../../utils/personTable';
import { listHrContracts, listHrPeople } from '../../api/hr';
import { formatNumber, formatDate } from '../../utils/format';
import { cellFteStyle } from './fteColor';
import {
  MONTHS, SORT_COLUMNS, fmtMonthYear, fmtChfInt,
  buildPeopleViewSpan, monthlyTotals,
} from './peopleMonthly';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import SearchField from '../../components/SearchField';
import PersonName from '../../components/PersonName';
import SegmentedToggle from '../../components/SegmentedToggle';
import PersonTh from '../../components/PersonTh';
import './HrPage.css';

/* Effectifs — grille mensuelle détaillée par personne (taux d'activité mois par
   mois, entrées/sorties, totaux FTE & headcount). Extrait de la Vue d'ensemble :
   le détail vit ici, la Synthèse ne garde que les agrégats. */

export default function HrHeadcountPage({ activeTenant, year: yearProp, embedded = false,
                                          onDataReady = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const year = yearProp ?? new Date().getFullYear();

  const [contracts, setContracts] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = usePersistentValue(`hr_headcount_status_v2_${tenantId}`, 'all');
  /* Fenêtre affichée : l'exercice seul, ou deux ans en arrière comme en avant
     — même choix que la Synthèse des effectifs (2026-08-29), pour qu'on ne
     règle pas la même chose de deux façons dans une même section. Sur deux
     ans, les fins de contrat déjà saisies se voient sans changer d'exercice.
     C'est une VUE : elle élargit ce qu'on dessine, elle ne retranche pas de
     ligne. */
  const [win, setWin] = usePersistentValue(`hr_headcount_win_${tenantId}`, 'n');
  const [search, setSearch] = useState('');
  const sort = useTableSort(`hr_headcount_sort_${tenantId}`, 'entry_date', 'desc');
  const { sortCol, sortDir, handleSort, sortRows, nameOrder } = sort;

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([listHrContracts(tenantId), listHrPeople(tenantId)])
      .then(([c, p]) => {
        if (!c.error) setContracts(c.data || []);
        if (!p.error) setPeople(p.data || []);
      })
      /* `onDataReady` sert l'impression : le document attend le signal de
         chaque rapport avant d'ouvrir la fenêtre, sinon il imprime une page
         encore vide. */
      .finally(() => { setLoading(false); if (onDataReady) onDataReady('headcount'); });
  }, [tenantId]);

  const years = useMemo(() => (
    win === 'prev' ? [year - 1, year] : win === 'next' ? [year, year + 1] : [year]
  ), [win, year]);
  const monthCols = useMemo(
    // Libellé = le mois seul : l'année est portée par le bandeau au-dessus.
    () => years.flatMap(y => MONTHS.map((m, i) => ({ year: y, mi: i, label: m }))),
    [years],
  );
  const peopleView = useMemo(() => buildPeopleViewSpan(people, contracts, years), [people, contracts, years]);

  const filteredPeople = useMemo(() => {
    let arr = peopleView;
    if (statusFilter === 'active') arr = arr.filter(p => p.is_active);
    const q = normalizeName(search.trim());
    if (q) arr = arr.filter(p => p._search.includes(q));
    return arr;
  }, [peopleView, statusFilter, search]);

  const sortedPeople = useMemo(
    () => sortRows(filteredPeople),
    [filteredPeople, sortCol, sortDir], // eslint-disable-line
  );

  const { monthlyFte, monthlyHeadcount, avgFte, avgHeadcount } = useMemo(
    () => monthlyTotals(sortedPeople, monthCols.length), [sortedPeople, monthCols.length],
  );

  // Filet vertical au 1er mois d'une année suivante : la frontière se voit.
  const monthCls = (i) => `hr-evol__mcol${i > 0 && monthCols[i].mi === 0 ? ' hr-evol__ystart' : ''}`;
  const cellCls = (i) => (i > 0 && monthCols[i].mi === 0 ? ' hr-evol__ystart' : '');
  const spanLabel = years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : `${year}`;
  /* Sur deux ans, vingt-quatre colonnes se partagent la largeur : l'EPT s'y
     arrondit à l'unité (2026-08-30). La décimale se lit encore au survol, et
     sur un an où la place ne manque pas. */
  const fteDigits = years.length > 1 ? 0 : 1;

  if (!tenantId) {
    return (
      <PageShell embedded={embedded} framed={!embedded} title={p('Effectifs')}>
        <div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div>
      </PageShell>
    );
  }

  return (
    <PageShell embedded={embedded} framed={!embedded} title={p('Effectifs')}>
      <main className="hr-page hr-overview is-embedded">
        {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
        {!loading && (
          /* Standard de carte : la PÉRIODE est une portée (elle élargit la
             fenêtre lue), elle va donc dans la toolbar avec la recherche ; le
             statut est un filtre, il RÉDUIT les lignes. */
          <CardShell
            flush
            title={p('Évolution du personnel')}
            subtitle={spanLabel}
            viewControls={(
              <SegmentedToggle
                compact
                label={p('Période')} value={win} onChange={setWin}
                options={[
                  { value: 'prev', label: `${year - 1} · ${year}` },
                  { value: 'n',    label: `${year}` },
                  { value: 'next', label: `${year} · ${year + 1}` },
                ]}
              />
            )}
            filters={(
              <>
                <FilterIcon />
                <SegmentedToggle
                  compact
                  label={p('Statut')} value={statusFilter} onChange={setStatusFilter}
                  options={[
                    { value: 'all', label: p('Tous') },
                    { value: 'active', label: p('Actifs'), tone: 'alert' },
                  ]}
                />
                <FilterCount
                  count={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
                  onClear={() => { setStatusFilter('all'); setSearch(''); }}
                />
              </>
            )}
            search={<SearchField value={search} onChange={setSearch} placeholder={p('Rechercher…')} />}
            filterCount={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
            collapsible storageKey="people-headcount"
          >

            <div className="hr-evol">
              {sortedPeople.length === 0 ? (
                <div className="hr-page__empty">Aucune personne dans la fenêtre {spanLabel}.</div>
              ) : (
                <div className="hr-evol__table-wrap">
                  <table className={`hr-evol__table${years.length > 1 ? ' hr-evol__table--span2' : ''}`}>
                    <thead>
                      {/* Bandeau d'année au-dessus de la ligne des mois, au
                          format standard (lk-band) : sur deux ans, on sait
                          quel exercice on lit sans compter les colonnes. */}
                      <tr className="hr-evol__yband">
                        <th className="hr-evol__yband-pad" colSpan={SORT_COLUMNS.length} />
                        {years.map((y, yi) => (
                          <th key={y}
                              className={`lk-band${yi > 0 ? ' hr-evol__yband--next hr-evol__ystart' : ''}`}
                              colSpan={MONTHS.length}>{y}</th>
                        ))}
                      </tr>
                      <tr>
                        {SORT_COLUMNS.map(col => {
                          if (col.personSort) {
                            return <PersonTh key={col.key} sort={sort} className="hr-evol__sort-th" />;
                          }
                          const isSorted = sortCol === col.key;
                          const cls = `hr-evol__sort-th${col.align === 'right' ? ' hr-evol__num' : ''}${isSorted ? ' is-sorted' : ''}${col.detail ? ' hr-evol__col-detail' : ''}`;
                          return (
                            <th key={col.key} className={cls} onClick={() => handleSort(col.key)} title={p('Cliquer pour trier')}>
                              <span>{col.label}</span>
                              <span className="hr-evol__sort-ind">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                              {col.sub && <span className="hr-evol__sub">{col.sub}</span>}
                            </th>
                          );
                        })}
                        {monthCols.map((c, i) => (
                          <th key={i} className={monthCls(i)}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPeople.map(person => (
                        <tr key={person.id}>
                          <td className="hr-evol__name">
                            <PersonName firstName={person.first_name} lastName={person.last_name} order={nameOrder} />
                          </td>
                          <td className={`hr-evol__col-detail ${person.department ? '' : 'hr-page__muted'}`}>{person.department || '—'}</td>
                          <td className="hr-evol__num hr-evol__col-detail">{person.salary100 > 0 ? fmtChfInt(person.salary100) : '—'}</td>
                          <td className="hr-evol__col-detail">{fmtMonthYear(person.entry_date)}</td>
                          <td className="hr-evol__col-detail">{fmtMonthYear(person.termination_date)}</td>
                          {person.cells.map((cell, i) => (
                            <td
                              key={i}
                              className={`hr-evol__cell hr-evol__cell--${cell.kind}${cellCls(i)}`}
                              style={cellFteStyle(cell.kind, cell.pct)}
                              title={
                                cell.kind === 'before' ? p('Arrivée prévue {{date}}', { date: formatDate(person.entry_date) }) :
                                cell.kind === 'after'  ? p('Départ prévu {{date}}', { date: formatDate(person.termination_date) }) :
                                cell.kind === 'none'   ? p('Aucun contrat actif ce mois') :
                                `${cell.pct}% — ${MONTHS[monthCols[i].mi]} ${monthCols[i].year}`
                              }
                            >
                              {cell.pct}<span className="hr-evol__pct-sign">%</span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="hr-evol__total-row hr-evol__total-row--fte">
                        <th scope="row" colSpan={SORT_COLUMNS.length} className="hr-evol__total-label hr-page__no-print">
                          <span className="hr-evol__total-title">{p('Total EPT')}</span>
                          <span className="hr-evol__total-avg">moyenne {formatNumber(avgFte, 1)}</span>
                        </th>
                        <th scope="row" className="hr-evol__total-label hr-evol__print-only">
                          <span className="hr-evol__total-title">EPT · moy. {formatNumber(avgFte, 1)}</span>
                        </th>
                        {monthlyFte.map((v, i) => (
                          <td key={i} className={`hr-evol__total-cell${cellCls(i)}`} title={`${formatNumber(v, 2)} EPT — ${MONTHS[monthCols[i].mi]} ${monthCols[i].year}`}>
                            {formatNumber(v, fteDigits)}
                          </td>
                        ))}
                      </tr>
                      <tr className="hr-evol__total-row hr-evol__total-row--hc">
                        <th scope="row" colSpan={SORT_COLUMNS.length} className="hr-evol__total-label hr-page__no-print">
                          <span className="hr-evol__total-title">{p('Effectif total')}</span>
                          <span className="hr-evol__total-avg">moyenne {formatNumber(avgHeadcount, 1)}</span>
                        </th>
                        <th scope="row" className="hr-evol__total-label hr-evol__print-only">
                          <span className="hr-evol__total-title">Eff. · moy. {formatNumber(avgHeadcount, 1)}</span>
                        </th>
                        {monthlyHeadcount.map((n, i) => (
                          <td key={i} className={`hr-evol__total-cell${cellCls(i)}`} title={`${n} personne${n > 1 ? 's' : ''} active${n > 1 ? 's' : ''} — ${MONTHS[monthCols[i].mi]} ${monthCols[i].year}`}>
                            {n}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Page 2 (print only) : fiche détail des personnes */}
            {sortedPeople.length > 0 && (
              <div className="hr-evol hr-evol--print-detail">
                <div className="hr-evol__head">
                  <div className="hr-evol__title">
                    DÉTAIL DU PERSONNEL · {spanLabel}
                    <span className="hr-evol__hint">{p('département · salaire 100% · entrée / sortie')}</span>
                  </div>
                </div>
                <table className="hr-evol__table hr-evol__table--detail">
                  <thead>
                    <tr>
                      <th>{t('phrases:p_984f5046d6')}</th>
                      <th>{t('phrases:p_3d7c87c2af')}</th>
                      <th className="hr-evol__num">{p('100% (CHF/m)')}</th>
                      <th>{t('phrases:p_74ec72cc90')}</th>
                      <th>{p('Date de sortie')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPeople.map(person => (
                      <tr key={person.id}>
                        <td className="hr-evol__name">
                          <PersonName firstName={person.first_name} lastName={person.last_name} />
                        </td>
                        <td className={person.department ? '' : 'hr-page__muted'}>{person.department || '—'}</td>
                        <td className="hr-evol__num">{person.salary100 > 0 ? fmtChfInt(person.salary100) : '—'}</td>
                        <td>{fmtMonthYear(person.entry_date)}</td>
                        <td>{fmtMonthYear(person.termination_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardShell>
        )}
      </main>
    </PageShell>
  );
}
